import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  ArrowRight,
  Check,
  MapPin,
  Minus,
  Plus,
  Search,
  ShoppingBag,
  UtensilsCrossed,
  X,
} from "lucide-react";

import {
  NOVA_MENU_CATEGORIES,
  NOVA_MENU_ITEMS,
  type NovaMenuCategory,
  type NovaMenuGroup,
  type NovaMenuItem,
} from "../data/nova-menu";

import {
  loadNovaQrMenuCatalog,
} from "../data/nova-menu-repository";

import {
  submitNovaOnlineOrder,
} from "./nova-online-order";

import {
  buildNovaQrOrderDraft,
  type NovaQrFulfilment,
  type NovaQrOrderDraft,
  type NovaQrOrderLine,
} from "./qr-order";

type QrMenuView =
  | "all"
  | NovaMenuGroup;

function formatPrice(
  value: number,
) {
  return `KES ${value.toLocaleString(
    "en-KE",
  )}`;
}

function normalizeTableNumber(
  raw: string | null,
) {
  if (!raw) {
    return null;
  }

  const cleaned = raw
    .trim()
    .replace(
      /[^a-zA-Z0-9-]/g,
      "",
    )
    .slice(0, 12);

  return cleaned || null;
}

function itemPriceLabel(
  item: NovaMenuItem,
) {
  if (
    typeof item.price === "number"
  ) {
    return formatPrice(
      item.price,
    );
  }

  if (item.variants?.length) {
    const minimum = Math.min(
      ...item.variants.map(
        (variant) =>
          variant.price,
      ),
    );

    return `From ${formatPrice(
      minimum,
    )}`;
  }

  return "Ask us";
}

function itemUnitPrice(
  item: NovaMenuItem,
  variantId?: string,
) {
  if (
    typeof item.price === "number"
  ) {
    return item.price;
  }

  if (
    item.variants?.length &&
    variantId
  ) {
    return (
      item.variants.find(
        (variant) =>
          variant.id ===
          variantId,
      )?.price ?? 0
    );
  }

  return 0;
}

function cartKey(
  itemId: string,
  variantId?: string,
) {
  return `${itemId}::${
    variantId ?? "base"
  }`;
}

function BotanicalCorner({
  className = "",
  flip = false,
}: {
  className?: string;
  flip?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 220 260"
      fill="none"
      aria-hidden="true"
      className={className}
      style={{
        transform:
          flip
            ? "scaleX(-1)"
            : undefined,
      }}
    >
      <path
        d="M20 250C46 205 61 159 67 109C72 68 89 36 124 10"
        stroke="currentColor"
        strokeWidth="1.4"
      />

      <path
        d="M63 135C42 127 25 111 15 89C41 89 60 98 70 116"
        stroke="currentColor"
        strokeWidth="1.1"
      />

      <path
        d="M69 102C87 83 106 76 128 79C120 101 102 116 78 120"
        stroke="currentColor"
        strokeWidth="1.1"
      />

      <path
        d="M81 69C67 50 62 30 67 11C87 25 97 43 95 62"
        stroke="currentColor"
        strokeWidth="1.1"
      />

      <path
        d="M104 43C121 31 139 28 157 34C145 53 129 63 110 64"
        stroke="currentColor"
        strokeWidth="1.1"
      />

      <path
        d="M48 178C26 171 11 157 2 136C25 135 44 143 56 158"
        stroke="currentColor"
        strokeWidth="1.1"
      />

      <path
        d="M55 169C77 151 99 147 120 155C107 176 88 188 65 187"
        stroke="currentColor"
        strokeWidth="1.1"
      />

      <circle
        cx="126"
        cy="11"
        r="3"
        fill="currentColor"
      />
    </svg>
  );
}

type MenuItemCardProps = {
  item: NovaMenuItem;

  categoryLabel?: string;

  selectedVariantId?: string;

  quantity: number;

  onVariantChange: (
    variantId: string,
  ) => void;

  onAdd: () => void;

  onRemove: () => void;
};

function MenuItemCard({
  item,
  categoryLabel,
  selectedVariantId,
  quantity,
  onVariantChange,
  onAdd,
  onRemove,
}: MenuItemCardProps) {
  const requiresVariant =
    Boolean(
      item.variants?.length,
    );

  const canOrder =
    item.orderingEnabled &&
    (!requiresVariant ||
      Boolean(
        selectedVariantId,
      ));

  return (
    <article className="group py-2.5 sm:py-3">
      <div className={`grid items-start gap-4 ${
        item.image
          ? "grid-cols-[1fr_74px] sm:grid-cols-[1fr_100px] lg:grid-cols-[1fr_112px]"
          : "grid-cols-1"
      }`}>
        <div className="min-w-0">
          <div className="flex items-baseline gap-3">
            <h4 className="min-w-0 shrink font-['Cormorant_Garamond'] text-[17px] font-semibold leading-[1.02] text-[#171812] sm:text-[19px]">
              {item.name}
            </h4>

            <span
              aria-hidden="true"
              className="mb-[4px] hidden h-px min-w-5 flex-1 bg-[#315f3d]/18 sm:block"
            />

            <span className="shrink-0 font-['Cormorant_Garamond'] text-[16px] font-bold leading-none text-[#9a604d] sm:text-[18px]">
              {itemPriceLabel(
                item,
              ).replace(
                "KES ",
                "",
              )}
            </span>
          </div>

          {item.description && (
            <p className="mt-1.5 max-w-xl font-['Cormorant_Garamond'] text-[13px] leading-[1.35] text-[#43483c] sm:text-[14px]">
              {item.description}
            </p>
          )}

          {item.featured && (
            <p className="mt-2 text-[7px] font-bold uppercase tracking-[.18em] text-[#2e623b]">
              Nova favourite
            </p>
          )}

          {item.variants?.length ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {item.variants.map(
                (variant) => (
                  <button
                    key={
                      variant.id
                    }
                    type="button"
                    onClick={() =>
                      onVariantChange(
                        variant.id,
                      )
                    }
                    className={`flex items-center gap-2 border px-3 py-1.5 text-[8px] font-bold uppercase tracking-[.12em] transition-colors ${
                      selectedVariantId ===
                      variant.id
                        ? "border-[#285638] bg-[#285638] text-[#f7e3a9]"
                        : "border-[#285638]/35 bg-[#f8e6b2]/50 text-[#285638]"
                    }`}
                  >
                    <span>
                      {
                        variant.label
                      }
                    </span>

                    <span className="font-['IBM_Plex_Mono']">
                      {
                        variant.price
                      }
                    </span>
                  </button>
                ),
              )}
            </div>
          ) : null}

          <div className="mt-3 flex items-center justify-between gap-4">
            <span className="text-[7px] font-bold uppercase tracking-[.15em] text-[#55705a]">
              {categoryLabel ??
                "Nova menu"}
            </span>

            {item.orderingEnabled ? (
              <div className="flex items-center gap-2">
                {quantity > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={
                        onRemove
                      }
                      aria-label={`Remove one ${item.name}`}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#315f3d]/40 text-[#315f3d] active:scale-95"
                    >
                      <Minus
                        size={11}
                      />
                    </button>

                    <span className="min-w-4 text-center font-['IBM_Plex_Mono'] text-[9px] font-bold">
                      {quantity}
                    </span>
                  </>
                )}

                <button
                  type="button"
                  onClick={onAdd}
                  disabled={
                    !canOrder
                  }
                  aria-label={`Add one ${item.name}`}
                  className="flex min-h-10 items-center justify-center gap-1.5 rounded-full bg-[#315f3d] px-4 text-[8px] font-bold uppercase tracking-[.12em] text-[#f9e8b6] transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <Plus
                    size={10}
                  />

                  {quantity > 0
                    ? "Add"
                    : "Order"}
                </button>
              </div>
            ) : (
              <span className="text-[7px] font-bold uppercase tracking-[.14em] text-[#786d58]">
                Unavailable
              </span>
            )}
          </div>

          {requiresVariant &&
            !selectedVariantId && (
              <p className="mt-2 text-right text-[7px] font-bold uppercase tracking-[.13em] text-[#7d593c]">
                Choose a size first
              </p>
            )}
        </div>

        {item.image && (
          <div className="aspect-square overflow-hidden rounded-full border-[4px] border-[#efd180] bg-[#f8e7b6] shadow-[0_10px_28px_rgba(51,69,45,.12)]">
            <img
              src={item.image}
              alt={item.name}
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>
    </article>
  );
}

export function NovaQrMenu() {
  const params =
    new URLSearchParams(
      window.location.search,
    );

  const tableNumber =
    normalizeTableNumber(
      params.get("t"),
    );

  const [
    menuCategories,
    setMenuCategories,
  ] = useState<
    readonly NovaMenuCategory[]
  >(NOVA_MENU_CATEGORIES);

  const [
    menuItems,
    setMenuItems,
  ] = useState<
    readonly NovaMenuItem[]
  >(NOVA_MENU_ITEMS);

  useEffect(() => {
    let cancelled = false;

    void loadNovaQrMenuCatalog().then(
      (catalog) => {
        if (cancelled) {
          return;
        }

        setMenuCategories(
          catalog.categories,
        );

        setMenuItems(
          catalog.items,
        );

        if (import.meta.env.DEV) {
          console.info(
            `[Nova QR] catalog=${catalog.source} categories=${catalog.categories.length} items=${catalog.items.length}`,
          );
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, []);

  const categoryById =
    useMemo(
      () =>
        new Map(
          menuCategories.map(
            (category) => [
              category.id,
              category,
            ],
          ),
        ),
      [menuCategories],
    );

  const [menuView, setMenuView] =
    useState<QrMenuView>(
      "all",
    );

  const [
    activeCategory,
    setActiveCategory,
  ] = useState("all");

  const [searchOpen, setSearchOpen] =
    useState(false);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [
    selectedVariants,
    setSelectedVariants,
  ] = useState<
    Record<string, string>
  >({});

  const [
    quantities,
    setQuantities,
  ] = useState<
    Record<string, number>
  >({});

  const [cartOpen, setCartOpen] =
    useState(false);

  const [
    fulfilment,
    setFulfilment,
  ] =
    useState<NovaQrFulfilment>(
      tableNumber
        ? "dine-in"
        : "takeaway",
    );

  const [
    guestName,
    setGuestName,
  ] = useState("");

  const [
    guestPhone,
    setGuestPhone,
  ] = useState("");

  const [
    preparedOrder,
    setPreparedOrder,
  ] =
    useState<NovaQrOrderDraft | null>(
      null,
    );

  const [
    orderSubmitting,
    setOrderSubmitting,
  ] = useState(false);

  const [
    orderError,
    setOrderError,
  ] = useState<string | null>(
    null,
  );

  const [
    confirmedOrderCode,
    setConfirmedOrderCode,
  ] = useState<string | null>(
    null,
  );

  const visibleCategories =
    useMemo(
      () =>
        menuCategories
          .filter(
            (category) =>
              menuView === "all" ||
              category.group ===
                menuView,
          )
          .sort(
            (a, b) =>
              a.sortOrder -
              b.sortOrder,
          ),
      [
        menuView,
        menuCategories,
      ],
    );

  const visibleItems = useMemo(
    () => {
      const normalizedSearch =
        searchTerm
          .trim()
          .toLowerCase();

      return menuItems
        .filter(
          (item) =>
            item.available,
        )
        .filter((item) => {
          const category =
            categoryById.get(
              item.categoryId,
            );

          return (
            menuView === "all" ||
            category?.group ===
              menuView
          );
        })
        .filter(
          (item) =>
            activeCategory ===
              "all" ||
            item.categoryId ===
              activeCategory,
        )
        .filter((item) => {
          if (!normalizedSearch) {
            return true;
          }

          const category =
            categoryById.get(
              item.categoryId,
            );

          return [
            item.name,
            item.description ?? "",
            category?.label ?? "",
          ]
            .join(" ")
            .toLowerCase()
            .includes(
              normalizedSearch,
            );
        })
        .sort((a, b) => {
          const categoryA =
            categoryById.get(
              a.categoryId,
            )?.sortOrder ?? 0;

          const categoryB =
            categoryById.get(
              b.categoryId,
            )?.sortOrder ?? 0;

          if (
            categoryA !==
            categoryB
          ) {
            return (
              categoryA -
              categoryB
            );
          }

          return (
            a.sortOrder -
            b.sortOrder
          );
        });
    },
    [
      menuView,
      activeCategory,
      searchTerm,
      menuItems,
      categoryById,
    ],
  );

  const groupedItems =
    useMemo(
      () =>
        visibleCategories
          .map((category) => ({
            category,
            items:
              visibleItems.filter(
                (item) =>
                  item.categoryId ===
                  category.id,
              ),
          }))
          .filter(
            ({ items }) =>
              items.length > 0,
          ),
      [
        visibleCategories,
        visibleItems,
      ],
    );

  const orderLines =
    useMemo<NovaQrOrderLine[]>(
      () => {
        const lines: NovaQrOrderLine[] =
          [];

        for (
          const item of
          menuItems
        ) {
          if (
            item.variants?.length
          ) {
            for (
              const variant of
              item.variants
            ) {
              const key =
                cartKey(
                  item.id,
                  variant.id,
                );

              const quantity =
                quantities[key] ?? 0;

              if (
                quantity <= 0
              ) {
                continue;
              }

              lines.push({
                lineId: key,

                itemId:
                  item.id,

                itemName:
                  item.name,

                variantId:
                  variant.id,

                variantLabel:
                  variant.label,

                unitPrice:
                  variant.price,

                quantity,

                lineTotal:
                  variant.price *
                  quantity,
              });
            }

            continue;
          }

          const key =
            cartKey(
              item.id,
            );

          const quantity =
            quantities[key] ?? 0;

          if (
            quantity <= 0
          ) {
            continue;
          }

          const unitPrice =
            itemUnitPrice(
              item,
            );

          lines.push({
            lineId: key,

            itemId:
              item.id,

            itemName:
              item.name,

            variantId: null,

            variantLabel: null,

            unitPrice,

            quantity,

            lineTotal:
              unitPrice *
              quantity,
          });
        }

        return lines;
      },
      [
        quantities,
        menuItems,
      ],
    );

  const cartCount =
    orderLines.reduce(
      (sum, line) =>
        sum + line.quantity,
      0,
    );

  const cartTotal =
    orderLines.reduce(
      (sum, line) =>
        sum + line.lineTotal,
      0,
    );

  const setView = (
    view: QrMenuView,
  ) => {
    setMenuView(view);
    setActiveCategory("all");
  };

  const setVariant = (
    itemId: string,
    variantId: string,
  ) => {
    setSelectedVariants(
      (current) => ({
        ...current,
        [itemId]:
          variantId,
      }),
    );
  };

  const changeQuantity = (
    item: NovaMenuItem,
    delta: number,
  ) => {
    const variantId =
      item.variants?.length
        ? selectedVariants[
            item.id
          ]
        : undefined;

    if (
      item.variants?.length &&
      !variantId
    ) {
      return;
    }

    const key =
      cartKey(
        item.id,
        variantId,
      );

    setQuantities(
      (current) => {
        const next =
          Math.max(
            0,
            (current[key] ??
              0) +
              delta,
          );

        if (next === 0) {
          const {
            [key]: _removed,
            ...rest
          } = current;

          return rest;
        }

        return {
          ...current,
          [key]: next,
        };
      },
    );

    setPreparedOrder(null);
  };

  const changeLineQuantity = (
    lineId: string,
    delta: number,
  ) => {
    setQuantities(
      (current) => {
        const next =
          Math.max(
            0,
            (current[lineId] ??
              0) +
              delta,
          );

        if (next === 0) {
          const {
            [lineId]:
              _removed,
            ...rest
          } = current;

          return rest;
        }

        return {
          ...current,
          [lineId]: next,
        };
      },
    );

    setPreparedOrder(null);
  };

  const quantityForItem = (
    item: NovaMenuItem,
  ) => {
    const variantId =
      item.variants?.length
        ? selectedVariants[
            item.id
          ]
        : undefined;

    if (
      item.variants?.length &&
      !variantId
    ) {
      return 0;
    }

    return (
      quantities[
        cartKey(
          item.id,
          variantId,
        )
      ] ?? 0
    );
  };

  const canPrepareOrder =
    orderLines.length > 0 &&
    (
      fulfilment ===
        "dine-in"
        ? Boolean(
            tableNumber,
          )
        : Boolean(
            guestName.trim() &&
            guestPhone.trim()
          )
    );

  const prepareOrder = async () => {
    if (
      !canPrepareOrder ||
      orderSubmitting
    ) {
      return;
    }

    const draft =
      buildNovaQrOrderDraft({
        tableNumber,

        fulfilment,

        guestName,

        guestPhone,

        lines:
          orderLines,
      });

    setOrderSubmitting(true);
    setOrderError(null);

    try {
      const confirmation =
        await submitNovaOnlineOrder({
          serviceMode:
            fulfilment === "dine-in"
              ? "dine-in"
              : "pickup",

          tableNumber:
            fulfilment === "dine-in"
              ? tableNumber
              : null,

          guestName,
          guestPhone,

          lines:
            orderLines.map(
              (line) => ({
                itemId:
                  line.itemId,

                variantId:
                  line.variantId,

                quantity:
                  line.quantity,
              }),
            ),
        });

      window.sessionStorage.setItem(
        "nova-qr-order-draft",
        JSON.stringify(
          draft,
        ),
      );

      window.sessionStorage.setItem(
        "nova-last-order-code",
        confirmation.orderCode,
      );

      setConfirmedOrderCode(
        confirmation.orderCode,
      );

      setPreparedOrder(
        draft,
      );

      //
      // Clear the basket ONLY after
      // Supabase confirms the order.
      //
      setQuantities({});
    } catch (error) {
      setOrderError(
        error instanceof Error
          ? error.message
          : "We could not place your order. Your basket has been kept.",
      );
    } finally {
      setOrderSubmitting(false);
    }
  };

  const renderMenuItem = (
    item: NovaMenuItem,
  ) => (
    <MenuItemCard
      key={item.id}
      item={item}
                    categoryLabel={
                      categoryById.get(
                        item.categoryId,
                      )?.label
                    }
      selectedVariantId={
        selectedVariants[
          item.id
        ]
      }
      quantity={
        quantityForItem(
          item,
        )
      }
      onVariantChange={(
        variantId,
      ) =>
        setVariant(
          item.id,
          variantId,
        )
      }
      onAdd={() =>
        changeQuantity(
          item,
          1,
        )
      }
      onRemove={() =>
        changeQuantity(
          item,
          -1,
        )
      }
    />
  );

  return (
    <div className="min-h-screen bg-[#f5e4ad] text-[#161812] selection:bg-[#285b3a] selection:text-[#f8e9bc]">
      <header className="relative overflow-hidden border-b border-[#315f3d]/20 bg-[radial-gradient(circle_at_50%_35%,#f9edc8_0%,#f5e4ad_48%,#f2dca1_100%)]">
        <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-[150px] lg:block">
          <BotanicalCorner className="absolute -left-7 -top-8 h-[230px] w-[190px] text-[#315f3d]/55" />

          <BotanicalCorner
            flip
            className="absolute -left-16 top-[48%] h-[190px] w-[160px] rotate-[18deg] text-[#315f3d]/20"
          />

          <div className="absolute left-[52px] top-[54%] flex flex-col items-center text-[#315f3d]/65">
            <span className="h-10 w-px bg-current" />
            <span className="my-3 text-[7px]">◆</span>
            <span className="h-10 w-px bg-current" />
          </div>

          <p className="absolute left-[54px] top-[61%] text-[7px] font-bold uppercase leading-[2.1] tracking-[.32em] text-[#315f3d]/70">
            Food
            <br />
            People
            <br />
            Stories
          </p>
        </div>

        <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[150px] lg:block">
          <BotanicalCorner
            flip
            className="absolute -right-7 -top-8 h-[230px] w-[190px] text-[#315f3d]/55"
          />

          <BotanicalCorner className="absolute -right-16 top-[48%] h-[190px] w-[160px] -rotate-[18deg] text-[#315f3d]/20" />

          <div className="absolute right-[52px] top-[54%] flex flex-col items-center text-[#315f3d]/65">
            <span className="h-10 w-px bg-current" />
            <span className="my-3 text-[7px]">◆</span>
            <span className="h-10 w-px bg-current" />
          </div>

          <p className="absolute right-[48px] top-[61%] text-right text-[7px] font-bold uppercase leading-[2.1] tracking-[.32em] text-[#315f3d]/70">
            A table
            <br />
            always
            <br />
            belongs
          </p>
        </div>

        <div className="relative z-10 mx-auto max-w-6xl px-4 pb-2 pt-2 sm:px-6 sm:pb-3 lg:px-10">
          <div className="flex items-center justify-between gap-3">
            <a
              href="/"
              className="flex min-h-11 items-center gap-2 text-[8px] font-bold uppercase tracking-[.18em] text-[#315f3d] sm:text-[9px]"
            >
              <ArrowLeft size={13} />
              <span>Nova home</span>
            </a>

            {tableNumber ? (
              <div className="flex min-h-10 items-center rounded-full border border-[#315f3d]/35 bg-[#f9ebbf]/70 px-3 text-[8px] font-bold uppercase tracking-[.15em] text-[#315f3d] sm:px-4 sm:text-[9px]">
                Table {tableNumber}
              </div>
            ) : (
              <div className="flex min-h-10 items-center gap-2 text-[8px] font-bold uppercase tracking-[.15em] text-[#315f3d] sm:text-[9px]">
                <UtensilsCrossed size={12} />
                <span>Digital menu</span>
              </div>
            )}
          </div>

          <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
            <p className="text-[7px] font-semibold uppercase tracking-[.22em] text-[#315f3d]/70 sm:text-[8px]">
              www.novaresculina.com
            </p>

            <div className="relative w-[100px] sm:w-[118px] lg:w-[132px]">
              <img
                src="/images/nova/novatransparent.png"
                alt="Nova Res Culina"
                className="h-auto w-full object-contain"
              />
            </div>

            <div className="mt-1 flex items-center gap-3 text-[#315f3d]/55">
              <span className="h-px w-8 bg-current sm:w-12" />
              <span className="text-[10px]">◆</span>
              <span className="h-px w-8 bg-current sm:w-12" />
            </div>

            <h1
              className="mt-0.5 font-['Cormorant_Garamond'] text-[1.75rem] font-bold uppercase leading-[.88] tracking-[-.035em] text-[#174b32] sm:text-[2.2rem] lg:text-[2.55rem]"
              style={{
                fontFamily:
                  '"Cormorant Garamond", Georgia, serif',
              }}
            >
              {menuView === "drinks"
                ? "Drinks Menu"
                : "Food Menu"}
            </h1>

            <p className="mt-1 text-[6px] font-bold uppercase tracking-[.29em] text-[#9a604d]">
              Good food. Greater company.
            </p>

            {tableNumber && (
              <div className="mt-4 text-center">
                <p className="font-['Cormorant_Garamond'] text-[1.35rem] font-semibold leading-none text-[#174b32] sm:text-[1.55rem]">
                  Welcome, Table {tableNumber}
                </p>

                <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-[#315f3d]/25 bg-[#f8e8b7]/70 px-4 py-2 text-[8px] font-bold uppercase tracking-[.15em] text-[#315f3d]">
                  <UtensilsCrossed size={11} />
                  Your table is connected
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      <main>
        <section className="sticky top-0 z-30 border-b border-[#315f3d]/20 bg-[#f5e4ad]/95 shadow-[0_8px_24px_rgba(48,74,50,.05)] backdrop-blur-md">
          <div className="mx-auto max-w-6xl px-3 sm:px-6 lg:px-10">
            <div className="flex items-center justify-between gap-3 border-b border-[#315f3d]/15 py-1.5">
              <div
                className="flex min-w-0 gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                role="tablist"
                aria-label="Menu groups"
              >
                {(
                  [
                    [
                      "all",
                      "All",
                    ],
                    [
                      "food",
                      "Food",
                    ],
                    [
                      "drinks",
                      "Drinks",
                    ],
                  ] as const
                ).map(
                  ([
                    value,
                    label,
                  ]) => (
                    <button
                      key={value}
                      type="button"
                      role="tab"
                      aria-selected={
                        menuView ===
                        value
                      }
                      onClick={() =>
                        setView(
                          value,
                        )
                      }
                      className={`flex min-h-10 shrink-0 items-center rounded-full px-4 py-2 text-[8px] font-bold uppercase tracking-[.15em] transition-colors sm:min-h-11 sm:px-5 sm:text-[9px] ${
                        menuView ===
                        value
                          ? "bg-[#183d2a] text-[#f4edd7]"
                          : "border border-[#b7b99f] text-[#52664e]"
                      }`}
                    >
                      {label}
                    </button>
                  ),
                )}
              </div>

              <div className="hidden min-w-[320px] max-w-md flex-1 justify-end md:flex">
                <label className="flex h-11 w-full max-w-[390px] items-center gap-3 rounded-full border border-[#315f3d]/30 bg-[#f8e8b6]/45 px-5">
                  <Search
                    size={15}
                    className="shrink-0 text-[#315f3d]/75"
                  />

                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(event) =>
                      setSearchTerm(
                        event.target.value,
                      )
                    }
                    placeholder="Search dishes, drinks or categories..."
                    className="w-full bg-transparent text-[12px] italic text-[#315f3d] outline-none placeholder:text-[#315f3d]/55"
                  />
                </label>
              </div>

              <button
                type="button"
                onClick={() =>
                  setSearchOpen(
                    (current) =>
                      !current,
                  )
                }
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors md:hidden ${
                  searchOpen
                    ? "border-[#183d2a] bg-[#183d2a] text-[#f1ecd9]"
                    : "border-[#315f3d]/30 text-[#315f3d]"
                }`}
                aria-label="Search menu"
              >
                <Search size={14} />
              </button>
            </div>

            {searchOpen && (
              <div className="border-b border-[#d1cdb8] py-3 md:hidden">
                <div className="flex items-center gap-3 rounded-full border border-[#aeb198] bg-[#e9e4d1] px-4">
                  <Search
                    size={13}
                    className="shrink-0 text-[#76806c]"
                  />

                  <input
                    type="search"
                    value={
                      searchTerm
                    }
                    onChange={(
                      event,
                    ) =>
                      setSearchTerm(
                        event.target
                          .value,
                      )
                    }
                    placeholder="Search dishes, drinks or categories"
                    className="w-full bg-transparent py-3 text-sm text-[#183d2a] outline-none placeholder:text-[#8a8d79]"
                    autoFocus
                  />
                </div>
              </div>
            )}

            <div
              className="flex gap-1.5 overflow-x-auto py-1.5 scroll-smooth overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:gap-3"
              role="tablist"
              aria-label="Menu categories"
            >
              <button
                type="button"
                onClick={() =>
                  setActiveCategory(
                    "all",
                  )
                }
                className={`min-h-9 shrink-0 border-b px-2 pb-1 text-[8px] font-bold uppercase tracking-[.11em] transition-colors sm:px-3 sm:text-[9px] ${
                  activeCategory ===
                  "all"
                    ? "border-[#183d2a] text-[#183d2a]"
                    : "border-transparent text-[#7a806f]"
                }`}
              >
                All categories
              </button>

              {visibleCategories.map(
                (category) => (
                  <button
                    key={
                      category.id
                    }
                    type="button"
                    onClick={() =>
                      setActiveCategory(
                        category.id,
                      )
                    }
                    className={`min-h-9 shrink-0 border-b px-2 pb-1 text-[8px] font-bold uppercase tracking-[.11em] transition-colors sm:px-3 sm:text-[9px] ${
                      activeCategory ===
                      category.id
                        ? "border-[#183d2a] text-[#183d2a]"
                        : "border-transparent text-[#7a806f]"
                    }`}
                  >
                    {
                      category.label
                    }
                  </button>
                ),
              )}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden">
          <BotanicalCorner className="pointer-events-none absolute -left-20 top-10 h-[340px] w-[260px] rotate-[6deg] text-[#315f3d]/12" />

          <BotanicalCorner
            flip
            className="pointer-events-none absolute -right-20 top-[32rem] h-[350px] w-[270px] -rotate-[6deg] text-[#315f3d]/12"
          />

          <div className="relative z-10 mx-auto max-w-6xl px-4 py-3 pb-24 sm:px-6 sm:py-4 lg:px-10 lg:py-5">
            <div className="mb-3 flex items-center justify-between border-b border-[#315f3d]/15 pb-2 text-left">
              <p className="text-[8px] font-bold uppercase tracking-[.24em] text-[#356440]">
                Nova Res Culina
              </p>

              <h2
                className="mt-0.5 font-['Cormorant_Garamond'] text-[1.25rem] font-bold uppercase leading-none tracking-[-.02em] text-[#174b32] sm:text-[1.45rem]"
                style={{
                  fontFamily:
                    '"Trebuchet MS", Arial, sans-serif',
                }}
              >
                {activeCategory ===
                "all"
                  ? menuView ===
                    "drinks"
                    ? "Drinks Menu"
                    : menuView ===
                        "food"
                      ? "Food Menu"
                      : "Our Menu"
                  : categoryById.get(
                      activeCategory,
                    )?.label ??
                    "Menu"}
              </h2>

              <p className="mt-3 text-[8px] font-bold uppercase tracking-[.15em] text-[#6a715e]">
                {visibleItems.length}{" "}
                {visibleItems.length ===
                1
                  ? "item"
                  : "items"}
              </p>
            </div>

            {visibleItems.length ===
            0 ? (
              <div className="py-24 text-center">
                <p className="font-['Cormorant_Garamond'] text-3xl text-[#183d2a]">
                  Nothing matched.
                </p>

                <p className="mt-3 text-sm text-[#777d6d]">
                  Try another category
                  or search term.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm(
                      "",
                    );

                    setActiveCategory(
                      "all",
                    );
                  }}
                  className="mt-6 border-b border-[#183d2a] pb-1 text-[9px] font-semibold uppercase tracking-[.16em]"
                >
                  Reset menu
                </button>
              </div>
            ) : activeCategory !==
              "all" ? (
              <div className="relative mx-auto max-w-4xl border-y border-[#315f3d]/12 bg-[linear-gradient(180deg,rgba(255,248,220,.18),rgba(255,248,220,.05))] px-4 py-6 sm:px-7 sm:py-8">
                {visibleItems.map(
                  renderMenuItem,
                )}
              </div>
            ) : (
              <div className="grid gap-y-5 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-7">
                {groupedItems.map(
                  ({
                    category,
                    items,
                  }) => (
                    <section
                      key={
                        category.id
                      }
                      id={`qr-${category.id}`}
                      className="relative scroll-mt-32 overflow-hidden border-y border-[#315f3d]/12 bg-[linear-gradient(180deg,rgba(255,248,220,.20),rgba(255,248,220,.05))] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-7 lg:odd:border-r lg:odd:border-[#315f3d]/18"
                    >
                      <BotanicalCorner className="pointer-events-none absolute -left-16 -top-16 h-[190px] w-[165px] text-[#27603a]/30" />

                      <BotanicalCorner
                        flip
                        className="pointer-events-none absolute -right-16 -bottom-20 h-[190px] w-[165px] rotate-180 text-[#27603a]/30"
                      />

                      <div className="relative z-10">
                        <div className="relative z-10 mb-4 text-center sm:mb-5">
                          <p className="text-[6px] font-bold uppercase tracking-[.34em] text-[#9a604d]">
                            Nova Res Culina
                          </p>

                          <h3 className="mt-1 font-['Cormorant_Garamond'] text-[2rem] font-bold uppercase leading-none tracking-[-.02em] text-[#174b32] sm:text-[2.35rem]">
                            {category.label}
                          </h3>

                          <div className="mx-auto mt-3 flex items-center justify-center gap-3 text-[#315f3d]/60">
                            <span className="h-px w-10 bg-current" />
                            <span className="text-[7px] text-[#9a604d]">◆</span>
                            <span className="h-px w-10 bg-current" />
                          </div>

                          <p className="mt-2 text-[6px] font-bold uppercase tracking-[.28em] text-[#9a604d]">
                            {category.group === "food"
                              ? "From the Nova kitchen"
                              : "Poured at Nova"}
                          </p>
                        </div>

                        <div className="divide-y divide-[#315f3d]/15">
                          {items.map(
                            renderMenuItem,
                          )}
                        </div>
                      </div>
                    </section>
                  ),
                )}
              </div>
            )}
          </div>
        </section>

        <section className="border-y border-[#bec0a7] bg-[#183d2a] px-5 py-12 text-[#f1ecd9] sm:px-8">
          <div className="mx-auto grid max-w-5xl gap-8 sm:grid-cols-2 sm:items-center">
            <div>
              <p className="text-[8px] font-semibold uppercase tracking-[.22em] text-[#aebc98]">
                Dining at Nova
              </p>

              <h2 className="mt-3 font-['Cormorant_Garamond'] text-4xl leading-[.9]">
                Stay awhile.
                <br />
                <em className="text-[#c9c89e]">
                  We&apos;ll take care
                  of the table.
                </em>
              </h2>
            </div>

            <div className="sm:text-right">
              {tableNumber ? (
                <>
                  <p className="text-sm leading-6 text-[#d7ddce]/80">
                    This menu is linked
                    to Table{" "}
                    {tableNumber}. Items
                    you choose will keep
                    this table reference
                    with the order.
                  </p>

                  <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#718269] px-4 py-2 text-[9px] uppercase tracking-[.16em] text-[#d7ddce]">
                    <UtensilsCrossed
                      size={12}
                    />
                    Table{" "}
                    {tableNumber}
                  </div>
                </>
              ) : (
                <p className="text-sm leading-6 text-[#d7ddce]/80">
                  Scan the QR on your
                  table for dine-in
                  ordering, or use this
                  menu for takeaway.
                </p>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="relative overflow-hidden bg-[#eed38b]">
        <BotanicalCorner
          flip
          className="pointer-events-none absolute -bottom-24 -right-12 h-[290px] w-[245px] text-[#79906c]/20"
        />

        <div className="relative z-10 mx-auto max-w-5xl px-5 py-10 sm:px-8">
          <div className="flex flex-col justify-between gap-8 sm:flex-row sm:items-end">
            <div>
              <p className="font-['Cormorant_Garamond'] text-3xl font-semibold leading-none text-[#183d2a]">
                Nova Res Culina
              </p>

              <div className="mt-4 flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[.15em] text-[#747966]">
                <MapPin
                  size={12}
                />
                Nairobi, Kenya
              </div>
            </div>

            <div className="text-left sm:text-right">
              <p className="text-[8px] font-semibold uppercase tracking-[.18em] text-[#8a8b76]">
                Permanent digital menu
              </p>

              <p className="mt-2 text-xs text-[#676d5d]">
                Prices shown in Kenyan
                shillings.
              </p>
            </div>
          </div>

          <div className="mt-8 border-t border-[#c4c0aa] pt-5 text-[8px] font-semibold uppercase tracking-[.17em] text-[#8a8b76]">
            Menu availability may
            change throughout the day.
          </div>
        </div>
      </footer>

      {cartCount > 0 && (
        <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-0 right-0 z-40 px-3 sm:px-4">
          <div className="mx-auto max-w-lg">
            <button
              type="button"
              onClick={() =>
                setCartOpen(true)
              }
              className="flex w-full items-center justify-between rounded-full bg-[#183d2a] px-5 py-4 text-[#f1ecd9] shadow-[0_14px_40px_rgba(24,61,42,.28)]"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-8 min-w-8 items-center justify-center rounded-full bg-[#f1ecd9] px-2 font-['IBM_Plex_Mono'] text-[10px] font-semibold text-[#183d2a]">
                  {cartCount}
                </span>

                <span className="text-[10px] font-semibold uppercase tracking-[.16em]">
                  Review order
                </span>
              </div>

              <span className="font-['IBM_Plex_Mono'] text-[10px]">
                {formatPrice(
                  cartTotal,
                )}
              </span>
            </button>
          </div>
        </div>
      )}

      {cartOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-[#183d2a]/45 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-label="Nova order"
        >
          <button
            type="button"
            onClick={() =>
              setCartOpen(false)
            }
            className="absolute inset-0"
            aria-label="Close order"
          />

          <div className="relative flex h-full w-full max-w-md flex-col bg-[#f5e4ad] text-[#183d2a] shadow-2xl sm:max-w-lg">
            <div className="flex items-start justify-between border-b border-[#c9c5ad] px-5 py-6">
              <div>
                <p className="text-[8px] font-semibold uppercase tracking-[.2em] text-[#78806d]">
                  Your Nova order
                </p>

                <h2 className="mt-2 font-['Cormorant_Garamond'] text-4xl font-semibold leading-none">
                  For the table.
                </h2>
              </div>

              <button
                type="button"
                onClick={() =>
                  setCartOpen(false)
                }
                className="flex h-9 w-9 items-center justify-center rounded-full border border-[#b7b99f]"
                aria-label="Close order"
              >
                <X size={16} />
              </button>
            </div>

            {preparedOrder ? (
              <div className="flex flex-1 flex-col items-center justify-center px-7 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#183d2a]">
                  <Check size={22} />
                </span>

                <h3 className="mt-6 font-['Cormorant_Garamond'] text-4xl font-semibold">
                  Order confirmed.
                </h3>

                <p className="mt-4 max-w-xs text-sm leading-6 text-[#66705f]">
                  Your order has reached Nova and is now safely recorded.
                </p>

                <p className="mt-5 max-w-xs text-[9px] uppercase tracking-[.14em] text-[#8a8d79]">
                  {confirmedOrderCode
                    ? `Order ${confirmedOrderCode}`
                    : "Order confirmed"}
                </p>

                <button
                  type="button"
                  onClick={() =>
                    setPreparedOrder(
                      null,
                    )
                  }
                  className="mt-8 border-b border-[#183d2a] pb-1 text-[9px] font-semibold uppercase tracking-[.16em]"
                >
                  Edit order
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5 py-6">
                  <div className="space-y-5">
                    {orderLines.map(
                      (line) => (
                        <div
                          key={
                            line.lineId
                          }
                          className="border-b border-[#d1cdb8] pb-5"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="font-['Cormorant_Garamond'] text-2xl font-semibold leading-none">
                                {
                                  line.itemName
                                }
                              </p>

                              {line.variantLabel && (
                                <p className="mt-2 text-[8px] font-semibold uppercase tracking-[.15em] text-[#7b806f]">
                                  {
                                    line.variantLabel
                                  }
                                </p>
                              )}
                            </div>

                            <span className="font-['IBM_Plex_Mono'] text-[9px]">
                              {formatPrice(
                                line.lineTotal,
                              )}
                            </span>
                          </div>

                          <div className="mt-4 flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                changeLineQuantity(
                                  line.lineId,
                                  -1,
                                )
                              }
                              className="flex h-7 w-7 items-center justify-center rounded-full border border-[#aeb198]"
                              aria-label={`Remove one ${line.itemName}`}
                            >
                              <Minus
                                size={11}
                              />
                            </button>

                            <span className="min-w-5 text-center font-['IBM_Plex_Mono'] text-[10px]">
                              {
                                line.quantity
                              }
                            </span>

                            <button
                              type="button"
                              onClick={() =>
                                changeLineQuantity(
                                  line.lineId,
                                  1,
                                )
                              }
                              className="flex h-7 w-7 items-center justify-center rounded-full bg-[#183d2a] text-[#f1ecd9]"
                              aria-label={`Add one ${line.itemName}`}
                            >
                              <Plus
                                size={11}
                              />
                            </button>
                          </div>
                        </div>
                      ),
                    )}
                  </div>

                  <div className="mt-8">
                    <p className="text-[8px] font-semibold uppercase tracking-[.18em] text-[#7a806e]">
                      How are you ordering?
                    </p>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        disabled={
                          !tableNumber
                        }
                        onClick={() =>
                          setFulfilment(
                            "dine-in",
                          )
                        }
                        className={`border px-4 py-3 text-left transition-colors ${
                          fulfilment ===
                          "dine-in"
                            ? "border-[#183d2a] bg-[#183d2a] text-[#f1ecd9]"
                            : "border-[#b7b99f] text-[#5f6a5b]"
                        } disabled:cursor-not-allowed disabled:opacity-35`}
                      >
                        <span className="block text-[9px] font-semibold uppercase tracking-[.14em]">
                          Dine in
                        </span>

                        <span className="mt-1 block text-[10px] opacity-70">
                          {tableNumber
                            ? `Table ${tableNumber}`
                            : "Scan table QR"}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          setFulfilment(
                            "takeaway",
                          )
                        }
                        className={`border px-4 py-3 text-left transition-colors ${
                          fulfilment ===
                          "takeaway"
                            ? "border-[#183d2a] bg-[#183d2a] text-[#f1ecd9]"
                            : "border-[#b7b99f] text-[#5f6a5b]"
                        }`}
                      >
                        <span className="block text-[9px] font-semibold uppercase tracking-[.14em]">
                          Takeaway
                        </span>

                        <span className="mt-1 block text-[10px] opacity-70">
                          Collect at Nova
                        </span>
                      </button>
                    </div>
                  </div>

                  {fulfilment ===
                    "takeaway" && (
                    <div className="mt-8 space-y-5">
                      <label className="block">
                        <span className="text-[8px] font-semibold uppercase tracking-[.18em] text-[#7a806e]">
                          Your name
                        </span>

                        <input
                          type="text"
                          value={
                            guestName
                          }
                          onChange={(
                            event,
                          ) =>
                            setGuestName(
                              event.target
                                .value,
                            )
                          }
                          placeholder="Name"
                          className="mt-2 w-full border-b border-[#aaaE98] bg-transparent py-3 text-sm outline-none focus:border-[#183d2a]"
                        />
                      </label>

                      <label className="block">
                        <span className="text-[8px] font-semibold uppercase tracking-[.18em] text-[#7a806e]">
                          Phone
                        </span>

                        <input
                          type="tel"
                          value={
                            guestPhone
                          }
                          onChange={(
                            event,
                          ) =>
                            setGuestPhone(
                              event.target
                                .value,
                            )
                          }
                          placeholder="+254"
                          className="mt-2 w-full border-b border-[#aaae98] bg-transparent py-3 text-sm outline-none focus:border-[#183d2a]"
                        />
                      </label>
                    </div>
                  )}
                </div>

                <div className="border-t border-[#c9c5ad] bg-[#ebe5d1] px-5 py-5">
                  {orderError && (
                    <div className="mb-4 border border-[#9d725c]/40 bg-[#f6ead8] px-4 py-3 text-xs leading-5 text-[#7b4f3f]">
                      {orderError}
                      <div className="mt-1 text-[8px] font-semibold uppercase tracking-[.12em]">
                        Your basket has not been cleared.
                      </div>
                    </div>
                  )}
                  <div className="flex items-end justify-between gap-5">
                    <div>
                      <p className="text-[8px] font-semibold uppercase tracking-[.18em] text-[#7a806e]">
                        Total
                      </p>

                      <p className="mt-1 font-['Cormorant_Garamond'] text-3xl font-semibold">
                        {formatPrice(
                          cartTotal,
                        )}
                      </p>
                    </div>

                    <p className="text-right text-[8px] uppercase tracking-[.14em] text-[#858875]">
                      {cartCount}{" "}
                      {cartCount ===
                      1
                        ? "item"
                        : "items"}
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={
                      !canPrepareOrder ||
                      orderSubmitting
                    }
                    onClick={
                      prepareOrder
                    }
                    className="mt-5 flex w-full items-center justify-between bg-[#183d2a] px-5 py-4 text-[9px] font-semibold uppercase tracking-[.17em] text-[#f1ecd9] disabled:cursor-not-allowed disabled:opacity-35"
                  >
                    <span>
                      {orderSubmitting
                        ? "Placing order…"
                        : "Place order"}
                    </span>

                    <ArrowRight
                      size={14}
                    />
                  </button>

                  {!tableNumber &&
                    fulfilment ===
                      "dine-in" && (
                      <p className="mt-3 text-center text-[8px] uppercase tracking-[.13em] text-[#806f5e]">
                        Dine-in ordering
                        requires a table
                        QR.
                      </p>
                    )}

                  {fulfilment ===
                    "takeaway" &&
                    (!guestName.trim() ||
                      !guestPhone.trim()) && (
                      <p className="mt-3 text-center text-[8px] uppercase tracking-[.13em] text-[#806f5e]">
                        Name and phone
                        are required for
                        takeaway.
                      </p>
                    )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
